import {
    assessQbdtMigrationFulfillment,
    basisFinalizeVerdict,
    Fulfillment,
} from "@common/helpers/documentFulfillment";
import type { MigrationRequiredPeriod } from "@common/helpers/migrationRequiredPeriod";
import type {
    MigrationDoc,
    MigrationMatchResult,
} from "./migrationFileMatching.types";
import {
    basisLabels,
    collectMigrationStartWarnings,
    earliestReadyGlStart,
    missingBasisWarning,
} from "./migrationFileMatchingWarnings";
import { hasPendingParserWarnings } from "./migrationExtractionWarnings";
import {
    isAwaitingBasisSelection,
    sortUnverifiedDocsByUploadOrder,
    toBasisRequiredUnverified,
    toCollectedExtraction,
    toMatchedRow,
    toParserWarningUnverified,
    toUnverifiedDoc,
} from "./migrationFileMatchingConverters";

export * from "./migrationFileMatching.types";

function buildMatchResult({
    fulfillment,
    docsById,
    docs,
    pendingParserWarningDocs,
    requiredPeriod,
    hasAccrualLedger,
    isAccrualLedgerResolved,
}: {
    fulfillment: Fulfillment;
    docsById: Map<number, MigrationDoc>;
    docs: MigrationDoc[];
    pendingParserWarningDocs: MigrationDoc[];
    requiredPeriod: MigrationRequiredPeriod | null;
    hasAccrualLedger: boolean;
    isAccrualLedgerResolved: boolean;
}): MigrationMatchResult {
    const rows = fulfillment.rows.map((row) => toMatchedRow({ row, docsById }));
    const startWarnings = collectMigrationStartWarnings({
        matchedRows: rows,
        fulfillmentRows: fulfillment.rows,
        docsById,
        requiredPeriod,
    });
    const unmatchedUnverifiedDocs = fulfillment.unmatched.map((doc) =>
        isAwaitingBasisSelection({ doc, docsById })
            ? toBasisRequiredUnverified(doc)
            : toUnverifiedDoc({ doc, docsById }),
    );
    const unverifiedDocs = sortUnverifiedDocsByUploadOrder({
        unverifiedDocs: [
            ...pendingParserWarningDocs.map(toParserWarningUnverified),
            ...unmatchedUnverifiedDocs,
        ],
        docs,
    });

    // An omitted basis (no GL provided) is allowed under the any-of policy and
    // becomes a warning; omitting every basis leaves nothing to migrate and is a
    // hard block. Coverage gaps within a provided basis block separately via the
    // server validation, so they aren't considered here.
    const { omittedKeys, isNoBasisProvided } = basisFinalizeVerdict(
        fulfillment.rows,
        hasAccrualLedger,
    );
    const missingBasisWarnings =
        isNoBasisProvided || !isAccrualLedgerResolved
            ? []
            : omittedKeys.map(missingBasisWarning);

    const isBasisSelectionRequired =
        fulfillment.isDimensionSelectionRequired &&
        fulfillment.unmatched.some((doc) =>
            isAwaitingBasisSelection({ doc, docsById }),
        );

    return {
        rows,
        unverifiedDocs,
        isCoaCovered: fulfillment.rows.some(
            (row) => row.requirementKey === "coa" && row.status === "ready",
        ),
        uncoveredBasisLabels: isNoBasisProvided ? basisLabels(omittedKeys) : [],
        isBasisSelectionRequired,
        // Start-date warnings come first: the command bar displays only the
        // first warning, and the adopt-start-date button must sit next to the
        // message it acts on.
        finalizeWarnings: [...startWarnings, ...missingBasisWarnings],
        earliestGlStart: earliestReadyGlStart({ matchedRows: rows, docsById }),
    };
}

export function matchMigrationFiles({
    requiredPeriod,
    hasAccrualLedger,
    isAccrualLedgerResolved = true,
    docs,
}: {
    requiredPeriod: MigrationRequiredPeriod | null;
    hasAccrualLedger: boolean;
    // Whether the entity's ledgers have loaded. While unresolved `hasAccrualLedger`
    // fails open to `true`, so missing-basis warnings are held back to avoid a
    // spurious "won't be imported" flash that vanishes once ledgers arrive.
    isAccrualLedgerResolved?: boolean;
    docs: MigrationDoc[];
}): MigrationMatchResult {
    const docsById = new Map(docs.map((doc) => [doc.documentId, doc]));
    const pendingParserWarningDocs = docs.filter(
        (doc) => doc.status === "extracted" && hasPendingParserWarnings(doc),
    );
    const pendingParserWarningIds = new Set(
        pendingParserWarningDocs.map((doc) => doc.documentId),
    );
    const docsForFulfillment = docs.filter(
        (doc) => !pendingParserWarningIds.has(doc.documentId),
    );
    const fulfillment = assessQbdtMigrationFulfillment({
        requiredPeriod,
        hasAccrualLedger,
        extractions: docsForFulfillment.map(toCollectedExtraction),
    });

    return buildMatchResult({
        fulfillment,
        docsById,
        docs,
        pendingParserWarningDocs,
        requiredPeriod,
        hasAccrualLedger,
        isAccrualLedgerResolved,
    });
}
